import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Button,
  CssBaseline,
  Backdrop,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
  ListItemButton,
  Skeleton,
  Menu,
  MenuItem,
  Collapse,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "react-oidc-context";
import useLogout from "../../../hooks/authentication_hooks/useLogout";
import { useUserQuery } from "../../../hooks/api_hooks/useUserQuery";
import TopPromoBanner from "./home_page/TopPromoBanner";
import kloudLogo from "../../../assets/kloud-web-logo-blue.svg";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const auth = useAuth();
  const { handleLogout } = useLogout();

  const [pendingLogout, setPendingLogout] = useState(false);
  const [pricingAnchor, setPricingAnchor] = useState(null);
  const [mobilePricingOpen, setMobilePricingOpen] = useState(false);

  useEffect(() => {
    setMobilePricingOpen(false);
  }, [location.pathname]);

  const shouldFetchUser = auth.isAuthenticated;
  const { data, isFetching, isLoading } = useUserQuery({
    enabled: shouldFetchUser,
  });
  const user = data?.user;
  const subscriptionStatus = user?.stripe_data?.subscription?.toLowerCase?.();
  const isSubscribed =
    subscriptionStatus === "active" || subscriptionStatus === "inactive";
  const subLoading = isFetching || isLoading;
  const wasSubscribedRef = useRef(false);

  useEffect(() => {
    if (isSubscribed === true) wasSubscribedRef.current = true;
  }, [isSubscribed]);

  const effectiveSubscribed =
    typeof isSubscribed === "boolean" ? isSubscribed : wasSubscribedRef.current;
  const showDashboard = auth.isAuthenticated && effectiveSubscribed;

  useEffect(() => {
    const logout = async () => {
      if (typeof auth.removeUser === "function") await auth.removeUser();
      if (typeof auth.clearStaleState === "function")
        await auth.clearStaleState();
    };

    let bc;
    if ("BroadcastChannel" in window) {
      bc = new BroadcastChannel("auth");
      bc.onmessage = (e) => e.data === "logout" && logout();
    }

    const onStorage = (e) => {
      if (e.key === "authState" && e.newValue === "signOut") logout();
      if (e.key?.startsWith("oidc.user")) auth.getUser?.().catch(() => {});
    };

    window.addEventListener("storage", onStorage);
    return () => {
      bc?.close?.();
      window.removeEventListener("storage", onStorage);
    };
  }, [auth]);

  const signOut = async () => {
    setPendingLogout(true);
    await handleLogout();
  };

  if (loading) {
    return (
      <Backdrop sx={{ color: "#000", zIndex: 1 }} open>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  const centerMenuItems = [{ text: "Contact", path: "/contact" }];

  return (
    <>
      <CssBaseline />
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          top: 0,
          backgroundColor: "white",
          color: "black",
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
          transition: "box-shadow 0.25s ease",
          zIndex: 1200,
        }}
      >
        <TopPromoBanner />
        <Toolbar
          sx={{
            width: "100%",
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 4 },
            minHeight: { xs: 80, sm: 80, md: 80 },
          }}
        >
          {/* Logo */}
          <Box
            onClick={() => navigate("/")}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <Box
              component="img"
              src={kloudLogo}
              alt="Kloud Scan"
              sx={{ height: { xs: 54, sm: 54, md: 54 }, width: "auto" }}
            />
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 3 }}
            >
              {/* Pricing dropdown */}
              <Box>
                <Button
                  onClick={(e) => setPricingAnchor(e.currentTarget)}
                  sx={{
                    color: pricingAnchor ? "#09b2f0" : "black",
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover": {
                      color: "#09b2f0",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.95rem",
                      letterSpacing: "0px",
                      lineHeight: 1.2,
                    }}
                  >
                    Pricing
                  </Typography>
                  <ArrowDropDownIcon
                    sx={{
                      fontSize: ".95rem",
                      transition: "transform 0.2s ease",
                      transform: pricingAnchor
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                  />
                </Button>

                <Menu
                  anchorEl={pricingAnchor}
                  open={Boolean(pricingAnchor)}
                  onClose={() => setPricingAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  slotProps={{
                    paper: {
                      sx: {
                        paddingX: 0,
                        borderRadius: 2,
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                      },
                    },
                  }}
                >
                  <MenuItem
                    component={Link}
                    to="/pricing"
                    onClick={() => setPricingAnchor(null)}
                    sx={{
                      fontSize: ".9rem",
                      color: "black",
                      "&:hover": {
                        color: "#09b2f0",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    Individual Plan
                  </MenuItem>
                </Menu>
              </Box>

              {/* Center menu items */}
              {centerMenuItems.map(({ text, path }) => (
                <ListItemButton
                  key={text}
                  component={Link}
                  to={path}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    textDecoration: "none",
                    color: location.pathname === path ? "#09b2f0" : "black",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "#09b2f0",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.95rem",
                      letterSpacing: "0px",
                      lineHeight: 1.2,
                    }}
                  >
                    {text}
                  </Typography>
                </ListItemButton>
              ))}
            </Box>
          )}

          {/* Right side */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 0,
            }}
          >
            {isMobile ? (
              <IconButton
                edge="end"
                onClick={() => setDrawerOpen(true)}
                sx={{ ml: 1, color: "black" }}
              >
                <MenuIcon />
              </IconButton>
            ) : !auth.isAuthenticated && !pendingLogout && !auth.isLoading ? (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    localStorage.setItem("login_intent", "from_header");
                    window.location.href = "/login";
                  }}
                  sx={{
                    color: "black",
                    fontWeight: 600,
                    textTransform: "none",
                    px: 2.5,
                    borderRadius: 1.5,
                    borderColor: "#1f2937",
                    height: 40,
                    border: "none",
                    "&:hover": {
                      borderColor: "#3cc6ff",
                      backgroundColor: "rgba(79,70,229,0.06)",
                      color: "#3cc6ff",
                    },
                  }}
                >
                  Log in
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  onClick={(e) => {
                    e.currentTarget.blur();
                    localStorage.setItem("login_intent", "from_header");
                    window.location.href = "/pricing";
                  }}
                  sx={{
                    backgroundColor: "#f59e0b",
                    color: "#0b1220",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: 1,
                    boxShadow: "none",
                    ml: 1.8,
                    height: 40,
                    "&:hover": { backgroundColor: "#D97706" },
                    "&:focus": { backgroundColor: "#f59e0b" },
                    "&:focus-visible": { backgroundColor: "#f59e0b" },
                  }}
                >
                  Sign Up
                </Button>
              </>
            ) : auth.isAuthenticated || pendingLogout ? (
              <>
                {subLoading ? (
                  <Skeleton
                    variant="rounded"
                    width={180}
                    height={18}
                    sx={{ bgcolor: "rgba(0,0,0,0.05)", borderRadius: 8, mr: 1 }}
                  />
                ) : (
                  <>
                    {user?.email && (
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: "text.secondary",
                          mr: 1,
                          fontWeight: 500,
                        }}
                      >
                        {user.email}
                      </Typography>
                    )}

                    {showDashboard && (
                      <Button
                        component={Link}
                        to="/dashboard"
                        sx={{
                          color: "black",
                          fontWeight: 500,
                          textTransform: "none",
                          px: 1.2,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          "&:hover": { backgroundColor: "transparent" },
                        }}
                      >
                        <DashboardIcon sx={{ fontSize: 22 }} />
                        Dashboard
                      </Button>
                    )}

                    <Button
                      onClick={signOut}
                      sx={{
                        ml: 1.2,
                        color: "black",
                        fontWeight: 500,
                        textTransform: "none",
                        px: 1.2,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        "&:hover": { backgroundColor: "transparent" },
                      }}
                    >
                      <AccountCircleIcon sx={{ fontSize: 22 }} />
                      Sign out
                    </Button>
                  </>
                )}
              </>
            ) : null}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 260 }} role="presentation">
          {/* Top Bar with Logo + Close */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1.5,
              backgroundColor: "white",
            }}
          >
            <Box
              component="img"
              src={kloudLogo}
              alt="Kloud Scan"
              sx={{ height: 36, width: "auto" }}
            />
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon sx={{ color: "black" }} />
            </IconButton>
          </Box>

          <List sx={{ p: 0 }}>
            {/* Mobile Pricing */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setMobilePricingOpen((prev) => !prev)}
              >
                <ListItemText primary="Pricing" />
                {mobilePricingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>

            <Collapse in={mobilePricingOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton
                  component={Link}
                  to="/pricing"
                  selected={location.pathname === "/pricing"}
                  sx={{ pl: 4 }}
                  onClick={() => setDrawerOpen(false)}
                >
                  <ListItemText primary="Individual Plan" />
                </ListItemButton>
              </List>
            </Collapse>

            {/* Center menu items */}
            {centerMenuItems.map(({ text, path }) => (
              <ListItem key={text} disablePadding>
                <ListItemButton
                  component={Link}
                  to={path}
                  selected={location.pathname === path}
                  onClick={() => setDrawerOpen(false)}
                >
                  <ListItemText primary={text} />
                </ListItemButton>
              </ListItem>
            ))}

            {/* Dashboard */}
            {showDashboard && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/dashboard"
                  selected={location.pathname === "/dashboard"}
                >
                  <ListItemText primary="Dashboard" />
                </ListItemButton>
              </ListItem>
            )}

            {/* Divider */}
            <Box sx={{ mx: 2, borderTop: "1px solid rgba(0,0,0,0.3)" }} />

            {/* Auth Buttons */}
            <ListItem disablePadding>
              <ListItemButton
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  px: 2.5,
                  py: 1,
                }}
              >
                {!auth.isAuthenticated && !pendingLogout ? (
                  <>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => {
                        localStorage.setItem("login_intent", "from_header");
                        window.location.href = "/login";
                      }}
                      sx={{
                        color: "black",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 2,
                        borderColor: "#1f2937",
                        border: "none",
                        height: 40,
                        "&:hover": {
                          borderColor: "#3cc6ff",
                          backgroundColor: "rgba(79,70,229,0.06)",
                          color: "#3cc6ff",
                        },
                      }}
                    >
                      Log In
                    </Button>

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={(e) => {
                        e.currentTarget.blur();
                        localStorage.setItem("login_intent", "from_header");
                        window.location.href = "/pricing";
                      }}
                      sx={{
                        backgroundColor: "#f59e0b",
                        color: "#0b1220",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 2,
                        boxShadow: "none",
                        height: 40,
                        "&:hover": { backgroundColor: "#D97706" },
                        "&:focus": { backgroundColor: "#f59e0b" },
                        "&:focus-visible": { backgroundColor: "#f59e0b" },
                      }}
                    >
                      Sign Up
                    </Button>
                  </>
                ) : (
                  <>
                    {user?.email && (
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: "text.secondary",
                          fontWeight: 500,
                          alignSelf: "flex-start",
                        }}
                      >
                        {user.email}
                      </Typography>
                    )}
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={signOut}
                      sx={{
                        color: "black",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 2,
                        borderColor: "#1f2937",
                        height: 40,
                        "&:hover": {
                          borderColor: "#3cc6ff",
                          backgroundColor: "rgba(79,70,229,0.06)",
                          color: "#3cc6ff",
                        },
                      }}
                    >
                      Sign Out
                    </Button>
                  </>
                )}
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
